import React, { useState, useEffect } from "react";
import { useLoaderData, useNavigate } from "react-router";
import { observer } from "mobx-react";
import Order from "@models/order";
import { rendermixAPI, storesAPI } from "@api/main";
import { authStore, globalPopupStore } from "@stores/main";
import Modal from "@components/Modal/Modal";
import BillingAddressForm from "@components/BillingAddressForm/BillingAddressForm";
import KycVerificationModal from "@components/KycVerificationModal/KycVerificationModal";
import { BillingAddressData } from "@models/billing-address";
import { hasStates } from "@utils/states";
import { usePageTitle } from '../../hooks/usePageTitle';
import { analytics } from '../../services/analytics';
import { pageTrackingService } from '../../services/pageTracking';
import { PaymentMethodsResponse } from "@models/payment-method";
import Loader from "@components/Loader/Loader";
import { gtagService } from '../../services/gtag';
import Header from '../HomeRedesign/components/Header/Header';
import Footer from '../HomeRedesign/components/Footer/Footer';
import ScrollToTop from '../HomeRedesign/components/ScrollToTop/ScrollToTop';
import { useToast } from '../../contexts/ToastContext';
import { optimizeImageUrl } from '@utils/urlUtils';
import { formatDiscountPercentage } from '@utils/formatDiscountPercentage';
import styles from './CheckoutRedesign.module.scss';
import modalStyles from '../Payment/BillingAddressModal.module.scss';

const CheckoutRedesign: React.FC = observer(() => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const order = useLoaderData<Order>();
  const [selectedPayment, setSelectedPayment] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMethods, setIsLoadingMethods] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsResponse>({});
  const [showBillingAddressModal, setShowBillingAddressModal] = useState(false);
  const [showKycModal, setShowKycModal] = useState(false);
  const [showEnforceKycModal, setShowEnforceKycModal] = useState(false);
  const [enforceKycModalMessage, setEnforceKycModalMessage] = useState("");
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState<string | null>(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  // Track checkout view and begin checkout conversion
  useEffect(() => {
    pageTrackingService.trackCheckoutView(order.orderSessionId, order.totalItemsBuyPriceString);
    
    gtagService.trackBeginCheckout({
      currency: order.currency || 'USD',
      value: order.totalItemsBuyPrice,
      items: order.items.map(item => ({
        item_id: item.id,
        item_name: item.title,
        price: item.buyPrice,
        quantity: item.quantity
      }))
    });
  }, [order.orderSessionId, order.totalItemsBuyPriceString, order.currency, order.totalItemsBuyPrice, order.items]);

  useEffect(() => {
    if (order.globalMessage) {
      globalPopupStore.show(order.globalMessage);
    }
  }, [order.globalMessage]);

  useEffect(() => {
    // Load payment methods immediately when order is available
    // Auth is handled by API interceptor, no need to wait for authStore.user
    if (order.orderSessionId) {
      setIsLoadingMethods(true);
      storesAPI
        .getPayemntMethds(order.orderSessionId)
        .then((methods) => {
          setPaymentMethods(methods);
          // Auto-select first enabled method according to predefined order
          const paymentMethodOrder = ['upi', 'card', 'netbanking', 'wallet'];
          const enabledMethods = Object.keys(methods).filter(key => methods[key].enabled);
          // Sort enabled methods by predefined order
          const sortedMethods = enabledMethods.sort((a, b) => {
            const indexA = paymentMethodOrder.indexOf(a);
            const indexB = paymentMethodOrder.indexOf(b);
            if (indexA === -1 && indexB === -1) return 0;
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
          });
          if (sortedMethods.length > 0) {
            setSelectedPayment(prev => prev || sortedMethods[0]);
          }
        })
        .catch((error) => {
          console.error('Error loading payment methods:', error);
          globalPopupStore.show('Failed to load payment methods. Please try again.');
        })
        .finally(() => {
          setIsLoadingMethods(false);
        });
    }
  }, [order.orderSessionId]);

  usePageTitle({
    title: 'Checkout | OnePlay Store'
  });

  const handlePaymentSelect = (slug: string) => {
    setSelectedPayment(slug);
    pageTrackingService.trackPaymentMethodSelect(slug);
  };

  const isBillingAddressComplete = (): boolean => {
    const user = authStore.user;
    if (!user) return false;
    const address = user.billingAddress;
    if (!address) return false;
    const requiredFields: Array<keyof typeof address> = ["name", "country", "city", "street_address", "pin_code"];
    for (const field of requiredFields) {
      if (!address[field] || String(address[field]).trim() === "") return false;
    }
    if (address.country && hasStates(address.country)) {
      if (!address.state || String(address.state).trim() === "") return false;
    }
    return true;
  };

  const proceedToPayment = async (method: string) => {
    if (!isLoading) setIsLoading(true);

    const selectedMethod = paymentMethods[method];
    const provider = selectedMethod && selectedMethod.providers && selectedMethod.providers.length > 0
      ? selectedMethod.providers[0]
      : method;

    const { session: res } = await storesAPI.createPaySession(
      order.orderSessionId,
      provider,
      method
    );

    if (res.enforceKyc) {
      pageTrackingService.trackKycPrompted(
        order.orderSessionId,
        order.totalItemsBuyPriceString
      );
      const trimmed =
        typeof res.globalMessage === "string" ? res.globalMessage.trim() : "";
      setEnforceKycModalMessage(
        trimmed ||
          "Identity verification is required before you can continue to payment."
      );
      setPendingPaymentMethod(method);
      setShowEnforceKycModal(true);
      setIsLoading(false);
      return;
    }

    if (res.globalMessage) {
      globalPopupStore.show(res.globalMessage);
      setIsLoading(false);
      return;
    }

    switch (res.state) {
      case "wait":
        setTimeout(() => proceedToPayment(method), 5000);
        break;
      case "refresh":
        window.location.reload();
        break;
      case "redirect":
        // Gateway handoff — NOT a confirmed purchase; completion is
        // recorded on the /orders?order_id= return.
        pageTrackingService.trackPaymentInitiated(order.orderSessionId, order.totalItemsBuyPriceString, method);
        window.location.href = res.redirectPaymentUrl;
        break;
      case "failed":
        pageTrackingService.trackPaymentFailed(order.orderSessionId, method, "session_failed");
        showToast('Payment failed. Please try again.', 'error', 4000);
        setIsLoading(false);
        break;
    }
  };

  const handleEnforceKycProceedToKyc = () => {
    setShowEnforceKycModal(false);
    setShowKycModal(true);
  };

  const handleEnforceKycCancel = () => {
    pageTrackingService.trackKycSkipped();
    setShowEnforceKycModal(false);
    setEnforceKycModalMessage("");
    setPendingPaymentMethod(null);
  };

  const handleProceed = async (method: string) => {
    if (order.globalMessage) {
      globalPopupStore.show(order.globalMessage);
      return;
    }
    await authStore.fetchUser();
    if (authStore.user?.hasBillingAddress) {
      await proceedToPayment(method);
      return;
    }
    if (!isBillingAddressComplete()) {
      // Blocking step for first purchases — a real funnel stage.
      analytics.track('billing_address_prompted', {});
      setPendingPaymentMethod(method);
      setShowBillingAddressModal(true);
      return;
    }
    await proceedToPayment(method);
  };

  const handleBillingAddressSubmit = async (data: BillingAddressData) => {
    try {
      await rendermixAPI.updateBillingAddress(data);
      analytics.track('billing_address_submitted', { result: 'success' });
      await authStore.fetchUser();
      setShowBillingAddressModal(false);
      showToast('Billing address saved successfully!', 'success', 3000);
      if (pendingPaymentMethod) {
        const method = pendingPaymentMethod;
        setPendingPaymentMethod(null);
        await proceedToPayment(method);
      }
    } catch (error) {
      analytics.track('billing_address_submitted', { result: 'failure' });
      console.error('Error saving billing address:', error);
      showToast('Failed to save billing address. Please try again.', 'error', 4000);
    }
  };

  const moreThanOneQuantity = order.items.some(item => item.quantity > 1);

  return (
    <div className={styles.checkoutPage}>
      <Header />

      <main className={styles.mainContent}>
        <div className={styles.container}>
          <div className={styles.checkoutHeader}>
            <button
              className={styles.backButton}
              onClick={() => navigate('/cart')}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to Cart
            </button>
            <h1 className={styles.pageTitle}>Checkout</h1>
          </div>

          <div className={styles.checkoutGrid}>
            {/* Left Column - Order Summary */}
            <div className={styles.orderSummary}>
              <div className={styles.summaryCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.stepNumber}>1</div>
                  <h2 className={styles.cardTitle}>Order Summary</h2>
                </div>

                <div className={styles.itemsList}>
                  <div className={styles.itemsHeader}>
                    <span className={styles.itemsCount}>Items ({order.totalItems})</span>
                  </div>
                  <div className={styles.itemsContainer}>
                    {order.items.map((item, index) => {
                      const rawDiscountPct =
                        item.displayPrice > item.buyPrice
                          ? ((item.displayPrice - item.buyPrice) / item.displayPrice) * 100
                          : 0;
                      const discountLabel = formatDiscountPercentage(rawDiscountPct);
                      const showDiscountBadge = discountLabel !== '0';

                      return (
                        <div key={index} className={styles.orderItem}>
                          <div className={styles.itemImage}>
                            <img
                              src={optimizeImageUrl(item.poster, 120, 160) || `${process.env.PUBLIC_URL || ''}/images/product-placeholder.svg`}
                              alt={item.title}
                              loading="lazy"
                              onError={(e) => {
                                const target = e.currentTarget as HTMLImageElement;
                                target.onerror = null;
                                target.src = `${process.env.PUBLIC_URL || ''}/images/product-placeholder.svg`;
                              }}
                            />
                            {showDiscountBadge && (
                              <div className={styles.discountBadge}>
                                <span className={styles.discountPercentage}>
                                  {discountLabel}%
                                </span>
                                <span className={styles.discountOff}>OFF</span>
                              </div>
                            )}
                            {item.offerFromStore && (
                              <div className={styles.storeBadge}>
                                <img
                                  src={item.offerFromStore.icon || `${process.env.PUBLIC_URL}/images/icons/steam-icon.svg`}
                                  alt={item.offerFromStore.text || 'Store'}
                                />
                              </div>
                            )}
                          </div>
                        <div className={styles.itemDetails}>
                          <h3 className={styles.itemTitle}>{item.title}</h3>
                          {moreThanOneQuantity && (
                            <div className={styles.itemQuantity}>Quantity: {item.quantity}</div>
                          )}
                        </div>
                        <div className={styles.itemPrice}>
                          {item.displayPrice > item.buyPrice ? (
                            <>
                              <span className={styles.currentPrice}>{item.buyPriceString}</span>
                              <span className={styles.originalPrice}>{item.displayPriceString}</span>
                            </>
                          ) : (
                            <span className={styles.currentPrice}>{item.buyPriceString}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>

                <div className={styles.priceBreakdown}>
                  <div className={styles.priceRow}>
                    <span className={styles.priceLabel}>Subtotal</span>
                    <span className={styles.priceValue}>{order.totalItemsDisplayPriceString}</span>
                  </div>
                  {order.totalItemsDiscountValue > 0 && (
                    <div className={`${styles.priceRow} ${styles.discountRow}`}>
                      <span className={styles.priceLabel}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 1L3 3.5V7C3 10.5 5.5 13.5 8 14.5C10.5 13.5 13 10.5 13 7V3.5L8 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M6 8L7.5 9.5L10 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Discount Applied
                      </span>
                      <span className={styles.discountValue}>-{order.totalItemsDiscountValueString}</span>
                    </div>
                  )}
                  {order.payOfferItems && order.payOfferItems.length > 0 && (
                    <div className={styles.offerItems}>
                      {order.payOfferItems.map((offerItem: string, index: number) => (
                        <div key={index} className={styles.offerItem}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 1L3 3.5V7C3 10.5 5.5 13.5 8 14.5C10.5 13.5 13 10.5 13 7V3.5L8 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M6 8L7.5 9.5L10 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          {offerItem}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className={`${styles.priceRow} ${styles.totalRow}`}>
                    <span className={styles.totalLabel}>Total Payable</span>
                    <span className={styles.totalValue}>{order.totalItemsBuyPriceString}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Payment Methods */}
            <div className={styles.paymentSection}>
              <div className={styles.paymentCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.stepNumber}>2</div>
                  <h2 className={styles.cardTitle}>Choose Payment Method</h2>
                </div>

                {isLoadingMethods ? (
                  <div className={styles.loadingPaymentMethods}>
                    <Loader size={40} color="#2196F3" />
                    <p>Loading payment methods...</p>
                  </div>
                ) : Object.keys(paymentMethods).filter(key => paymentMethods[key].enabled).length > 0 ? (
                  <div className={styles.paymentOptions}>
                    {Object.entries(paymentMethods)
                      .filter(([key, method]) => method.enabled)
                      .sort(([keyA], [keyB]) => {
                        const paymentMethodOrder = ['upi', 'card', 'netbanking', 'wallet'];
                        const indexA = paymentMethodOrder.indexOf(keyA);
                        const indexB = paymentMethodOrder.indexOf(keyB);
                        if (indexA === -1 && indexB === -1) return 0;
                        if (indexA === -1) return 1;
                        if (indexB === -1) return -1;
                        return indexA - indexB;
                      })
                      .map(([methodKey, method]) => (
                        <div
                          key={methodKey}
                          className={`${styles.paymentOption} ${
                            selectedPayment === methodKey ? styles.paymentOptionActive : ''
                          }`}
                          onClick={() => handlePaymentSelect(methodKey)}
                        >
                          <div className={styles.paymentOptionContent}>
                            <div className={styles.paymentLogo}>
                              {methodKey === 'upi' ? (
                                <img
                                  src={`${process.env.PUBLIC_URL}/images/icons/upi-logo.svg`}
                                  alt="UPI"
                                  onError={(e) => {
                                    const target = e.currentTarget as HTMLImageElement;
                                    target.onerror = null;
                                    target.src = `${process.env.PUBLIC_URL}/images/icons/payment-placeholder.svg`;
                                  }}
                                />
                              ) : (
                                <div className={styles.paymentMethodIcon}>
                                  {methodKey === 'card' && <i className="fas fa-credit-card"></i>}
                                  {methodKey === 'netbanking' && <i className="fas fa-university"></i>}
                                  {methodKey === 'wallet' && <i className="fas fa-wallet"></i>}
                                  {methodKey === 'emi' && <i className="fas fa-credit-card"></i>}
                                </div>
                              )}
                            </div>
                            <div className={styles.paymentInfo}>
                              <div className={styles.paymentNameRow}>
                                <span className={styles.paymentName}>
                                  {methodKey === 'upi' ? 'UPI' :
                                    methodKey === 'emi' ? 'EMI' :
                                    methodKey.charAt(0).toUpperCase() + methodKey.slice(1)}
                                </span>
                                {method.available_offers && method.available_offers.length > 0 && (
                                  <span className={styles.paymentOfferBadge}>
                                    {method.available_offers.length}{' '}
                                    {method.available_offers.length === 1 ? 'offer' : 'offers'}
                                  </span>
                                )}
                              </div>
                              {method.description && (
                                <div className={styles.paymentDescription}>{method.description}</div>
                              )}
                              {method.convenience_fee > 0 && (
                                <div className={styles.convenienceFee}>
                                  Convenience fee: {((order.totalItemsBuyPrice * method.convenience_fee) / 100).toFixed(2)}
                                </div>
                              )}
                            </div>
                            <div className={styles.paymentRadio}>
                              <div className={`${styles.radioButton} ${selectedPayment === methodKey ? styles.radioButtonActive : ''}`}>
                                {selectedPayment === methodKey && (
                                  <div className={styles.radioButtonInner}></div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className={styles.loadingPaymentMethods}>
                    <p>No payment methods available</p>
                  </div>
                )}

                <div className={styles.proceedSection}>
                  <button
                    className={styles.proceedButton}
                    disabled={!selectedPayment || isLoading}
                    onClick={() => selectedPayment && handleProceed(selectedPayment)}
                  >
                    {isLoading ? (
                      <>
                        <Loader size={20} color="#FFFFFF" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <span>Proceed to Payment</span>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </>
                    )}
                  </button>

                  {selectedPayment &&
                    paymentMethods[selectedPayment]?.available_offers &&
                    paymentMethods[selectedPayment].available_offers.length > 0 && (
                      <div className={styles.paymentOffersSection}>
                        <div className={styles.paymentOffersTitle}>Available offers</div>
                        <div className={styles.paymentOffersList}>
                          {paymentMethods[selectedPayment].available_offers.map((offer, index) => (
                            <div key={index} className={styles.paymentOfferItem}>
                              <div className={styles.paymentOfferIcon}>
                                {offer.img ? (
                                  <img src={offer.img} alt="" className={styles.paymentOfferImg} />
                                ) : (
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                    <path
                                      d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                                      fill="#FFD700"
                                      stroke="#FFD700"
                                      strokeWidth="1"
                                    />
                                  </svg>
                                )}
                              </div>
                              <span className={styles.paymentOfferLabel}>{offer.lable}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {selectedPayment && paymentMethods[selectedPayment]?.enforce_kyc && (
                    <div className={styles.kycNoticeBanner} role="status">
                      <div className={styles.kycNoticeIcon} aria-hidden>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M14 2v5h5M9 13h6M9 17h4"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                      <div className={styles.kycNoticeContent}>
                        <span className={styles.kycNoticeTitle}>KYC required for this method</span>
                        <span className={styles.kycNoticeDescription}>
                          You will be asked to verify your identity before payment can be completed.
                        </span>
                      </div>
                    </div>
                  )}

                  <div className={styles.securityNote}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 1L3 3.5V7C3 10.5 5.5 13.5 8 14.5C10.5 13.5 13 10.5 13 7V3.5L8 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6 8L7.5 9.5L10 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Your payment information is secure and encrypted</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <ScrollToTop />

      {isLoading && (
        <div className={styles.loaderOverlay}>
          <Loader size={60} color="#2196F3" />
        </div>
      )}

      <KycVerificationModal
        isOpen={showKycModal}
        onClose={() => {
          setShowKycModal(false);
          setPendingPaymentMethod(null);
        }}
        onVerificationSuccess={async () => {
          setShowKycModal(false);
          const method = pendingPaymentMethod;
          if (method) {
            await proceedToPayment(method);
          }
        }}
      />

      <Modal
        isOpen={showEnforceKycModal}
        onClose={handleEnforceKycCancel}
      >
        <div className={modalStyles.billingModal}>
          <div className={modalStyles.modalHeader}>
            <h2 className={styles.enforceKycModalTitle}>KYC required</h2>
            <button
              type="button"
              className={modalStyles.closeButton}
              onClick={handleEnforceKycCancel}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className={modalStyles.modalBody}>
            <div className={modalStyles.modalDescription}>
              <span>{enforceKycModalMessage}</span>
            </div>
            <div className={styles.enforceKycActions}>
              <button
                type="button"
                className={`${styles.proceedButton} ${styles.enforceKycActionBtn}`}
                onClick={handleEnforceKycProceedToKyc}
              >
                Proceed to KYC
              </button>
              <button
                type="button"
                className={styles.enforceKycCancelBtn}
                onClick={handleEnforceKycCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showBillingAddressModal}
        onClose={() => {
          // Closed without saving — checkout drop-off at the address step.
          analytics.track('billing_address_dismissed', {});
          setShowBillingAddressModal(false);
          setPendingPaymentMethod(null);
        }}
      >
        <div className={modalStyles.billingModal}>
          <div className={modalStyles.modalHeader}>
            <h2 className={modalStyles.modalTitle}>Billing Address Required</h2>
            <button
              className={modalStyles.closeButton}
              onClick={() => {
                setShowBillingAddressModal(false);
                setPendingPaymentMethod(null);
              }}
            >
              ×
            </button>
          </div>
          <div className={modalStyles.modalBody}>
            <div className={modalStyles.modalDescription}>
              <span>
                {authStore.user?.billingAddress && !isBillingAddressComplete()
                  ? "Please complete your billing address. Some required fields are missing."
                  : "We need your billing address to process your payment securely. This information is required before proceeding to the payment gateway."}
              </span>
            </div>
            <BillingAddressForm
              initialData={authStore.user?.billingAddress || null}
              onSubmit={handleBillingAddressSubmit}
              submitButtonText="Continue to Payment"
              showCancelButton={false}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
});

export default CheckoutRedesign;

